import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RaildaysMap from "Raildays/Components/RaildaysMap.js";
import RaildaysScrollTopButton from "Raildays/Components/RaildaysScrollTopButton.js";
import {
	formatDistanceMilesKm,
	resolveRailwayLocation,
	resolveRailwayMediaUrl,
	resolveRailwayTypeColor,
	slugify
} from "Raildays/RaildaysUtils.js";
import {useCallback, useMemo} from "react";
import {useOutletContext, useSearchParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import CardSection from "Components/Container.js";
import Image from "Components/Image.js";
import Loader from "Components/Loader.js";
import RaildaysService from "Services/RaildaysService.js";

const TABS = [
	{label: "Railways", value: "railways"},
	{label: "Events", value: "events"},
	{label: "Experiences", value: "experiences"}
];
const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";
const MAP_HEIGHT = "calc(100vh - var(--raildays-content-top))";
const TYPE_COLOR_PALETTE = [
	"#e11d48",
	"#2563eb",
	"#16a34a",
	"#ea580c",
	"#7c3aed",
	"#0f766e",
	"#b45309",
	"#be185d",
	"#1d4ed8",
	"#15803d"
];

const resolveName = item => (
	item?.Name || "Untitled"
);

const resolveItemId = item => (
	item?.EventId || item?.Id || slugify(resolveName(item))
);

const resolveRailwayName = (item, railwaysByOrgId) => (
	item?.Railway?.Name ||
	item?.RailwayName ||
	railwaysByOrgId.get(String(item?.OrgId || ""))?.name ||
	""
);

const resolveLocation = item => {
	if (item?.Location) {
		const location = item?.Location;
		const parts = [location?.Town, location?.County, location?.Country]
			.filter(Boolean);
		if (parts.length) return parts.join(", ");
	}
	if (item?.Address) return resolveRailwayLocation(item);
	return "";
};

const resolveImage = item => (
	resolveRailwayMediaUrl(item?.Hero || item?.Image || item?.Logo)
);

const resolveList = value => {
	if (!value) return [];
	if (Array.isArray(value)) return value;
	return [];
};

const getItemLocation = item => {
	const location = item?.Location;
	if (!location) return null;
	const latitude = Number(location?.Latitude);
	const longitude = Number(location?.Longitude);
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
	return [latitude, longitude];
};

const haversineMiles = (pointA, pointB) => {
	if (!pointA || !pointB) return null;
	const toRad = deg => (deg * Math.PI) / 180;
	const [lat1, lon1] = pointA;
	const [lat2, lon2] = pointB;
	if ([lat1, lon1, lat2, lon2].some(val => Number.isNaN(val))) return null;
	const R = 3958.8;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const resolveDetailLink = (tab, item) => {
	if (tab === "railways") return `/railways/${slugify(resolveName(item))}`;
	if (tab === "events") return `/events/${resolveItemId(item)}`;
	if (tab === "experiences") return `/experiences/${resolveItemId(item)}`;
	return null;
};

const resolveActionLabel = tab => {
	if (tab === "railways") return "View railway";
	if (tab === "events") return "View event";
	if (tab === "experiences") return "View experience";
	return "View information";
};

const resolveCardActionLabel = tab => {
	if (tab === "railways") return "Visit Railway";
	if (tab === "events") return "View Event";
	if (tab === "experiences") return "View Experience";
	return "View More Information";
};

const parseISODate = value => {
	if (!value) return null;
	const parts = value.split("-");
	if (parts.length < 3) return null;
	const [year, month, day] = parts.map(Number);
	if (!year || !month || !day) return null;
	return new Date(Date.UTC(year, month - 1, day));
};

const formatDateLong = date => {
	if (!date) return "";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC"
	});
};

const resolveDateLabel = item => {
	const start = parseISODate(item?.StartDate);
	const end = parseISODate(item?.EndDate) || start;
	if (!start || !end) return "";
	if (start.getTime() === end.getTime()) return formatDateLong(start);
	return `${formatDateLong(start)} - ${formatDateLong(end)}`;
};

const resolveTypeLabel = item => {
	if (item?.Type?.Name) return item.Type.Name;
	if (!Array.isArray(item?.EventTypes)) return "";
	return item.EventTypes
		.map(type => type?.Name)
		.filter(Boolean)
		.join(", ");
};

const normaliseHexColor = value => {
	if (!value) return "";
	const trimmed = String(value).trim();
	if (!trimmed) return "";
	if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
	if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;
	return "";
};

const hashString = value => {
	const source = String(value || "");
	let hash = 0;
	for (let i = 0; i < source.length; i += 1) {
		hash = ((hash << 5) - hash) + source.charCodeAt(i);
		hash |= 0;
	}
	return Math.abs(hash);
};

const resolveSeedColor = value => (
	TYPE_COLOR_PALETTE[hashString(value) % TYPE_COLOR_PALETTE.length]
);

const resolveEventTypeColor = (item, railwayTypeColor = "") => {
	const primaryType = Array.isArray(item?.EventTypes) ? item.EventTypes[0] : null;
	const explicitTypeColor = normaliseHexColor(primaryType?.Colour);
	if (explicitTypeColor) return explicitTypeColor;
	const typeColor = normaliseHexColor(railwayTypeColor);
	if (typeColor) return typeColor;
	const mapColor = normaliseHexColor(item?.Railway?.Map?.Colour);
	if (mapColor) return mapColor;
	if (primaryType?.Id || primaryType?.Name) {
		return resolveSeedColor(`${primaryType?.Id || ""}:${primaryType?.Name || ""}`);
	}
	return "#444";
};

const resolveMarkerColor = (tab, item, railwayTypeColor = "") => {
	if (tab === "railways") return resolveRailwayTypeColor(item);
	return resolveEventTypeColor(item, railwayTypeColor);
};

const RaildaysSearchView = () => {
	const locationContext = useOutletContext() || {};
	const targetLocation = locationContext.targetLocation || null;
	const [searchParams, setSearchParams] = useSearchParams();
	const query = (searchParams.get("search") || "").trim();
	const tab = searchParams.get("type") || "railways";

	const {
		data: searchData,
		error: searchError,
		loading: searchLoading
	} = useData(useCallback(
		() => (query ? RaildaysService.getSearchResults(query) : Promise.resolve(null)),
		[query]
	));

	const resolvedSearchData = searchData;

	const railways = useMemo(() => resolveList(resolvedSearchData?.Railways), [resolvedSearchData]);
	const events = useMemo(() => resolveList(resolvedSearchData?.Events), [resolvedSearchData]);
	const experiences = useMemo(() => resolveList(resolvedSearchData?.Experiences), [resolvedSearchData]);
	const {data: railwaysData} = useData(useCallback(
		() => RaildaysService.getRailways(),
		[]
	));
	const allRailways = useMemo(() => {
		if (!railwaysData) return [];
		if (Array.isArray(railwaysData)) return railwaysData;
		if (Array.isArray(railwaysData.objects)) return railwaysData.objects;
		return [];
	}, [railwaysData]);
	const railwaysByOrgId = useMemo(() => {
		const index = new Map();
		allRailways.forEach(railway => {
			const orgId = railway?.Id;
			if ((orgId === undefined) || (orgId === null)) return;
			index.set(String(orgId), {
				name: railway?.Name || "",
				typeColor: resolveRailwayTypeColor(railway)
			});
		});
		return index;
	}, [allRailways]);

	const items = useMemo(() => {
		if (tab === "events") return events;
		if (tab === "experiences") return experiences;
		return railways;
	}, [tab, railways, events, experiences]);

	const itemsWithDistance = useMemo(() => {
		const withDistance = items.map(item => {
			const position = getItemLocation(item);
			const distance = targetLocation ?
				haversineMiles([targetLocation.lat, targetLocation.lng], position) :
				null;
			return {
				...item,
				__distance: distance
			};
		});

		if (!targetLocation) return withDistance;

		return withDistance.sort((a, b) => {
			if (a.__distance === null && b.__distance === null) return 0;
			if (a.__distance === null) return 1;
			if (b.__distance === null) return -1;
			return a.__distance - b.__distance;
		});
	}, [items, targetLocation]);

	const counts = {
		railways: railways.length,
		events: events.length,
		experiences: experiences.length
	};

	const mapMarkers = useMemo(() => (
		itemsWithDistance
			.map((item, index) => {
				const position = getItemLocation(item);
				if (!position) return null;
				const name = resolveName(item);
				const id = resolveItemId(item);
				const railwayMeta = railwaysByOrgId.get(String(item?.OrgId || ""));
				const railwayName = resolveRailwayName(item, railwaysByOrgId);
				const location = resolveLocation(item);
				const link = resolveDetailLink(tab, item);
				const dateLabel = resolveDateLabel(item);
				const typeLabel = resolveTypeLabel(item);
				const distanceLabel =
					(typeof item?.__distance === "number" && !Number.isNaN(item.__distance)) ?
						formatDistanceMilesKm(item.__distance) :
						"";
				return {
					id: `${id}-${position[0]}-${position[1]}-${index}`,
					name,
					railwayName,
					location,
					subname: [railwayName, location].filter(Boolean).join(", "),
					dateLabel,
					typeLabel,
					distanceLabel,
					position,
					color: resolveMarkerColor(tab, item, railwayMeta?.typeColor),
					image: resolveImage(item),
					actionLabel: resolveActionLabel(tab),
					link
				};
			})
			.filter(Boolean)
	), [itemsWithDistance, railwaysByOrgId, tab]);

	const mapBounds = useMemo(() => (
		mapMarkers.map(marker => marker.position)
	), [mapMarkers]);

	const handleTabChange = nextTab => {
		setSearchParams(prev => {
			const nextParams = new URLSearchParams(prev);
			nextParams.set("type", nextTab);
			return nextParams;
		});
	};

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}>
			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
				<Box sx={{px: {xs: 2, md: 4, lg: 6}}}>
					<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
						<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
						<Typography color="#e2b534">Search Results</Typography>
					</Breadcrumbs>
					<Typography variant="h4" sx={{mt: 2, fontWeight: 700, textTransform: "uppercase"}}>
						Search Results{query ? ` for "${query}"` : ""}
					</Typography>
					<Typography sx={{mt: 2}}>
						You can search for a railway by its name or the town that it is in.
					</Typography>
				</Box>

				<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 4}}>
					<Stack direction={{xs: "column", md: "row"}} spacing={2}>
						{TABS.map(item => (
							<Button
								key={item.value}
								label={`${item.label} (${counts[item.value]})`}
								variant="text"
								onClick={() => handleTabChange(item.value)}
								style={{
									flex: 1,
									textTransform: "none",
									borderBottom: "2px solid",
									borderColor: tab === item.value ? "#3b82f6" : "transparent",
									borderRadius: 0,
									color: tab === item.value ? "#111827" : "#6b7280"
								}} />
						))}
					</Stack>
				</Box>

				{searchError && (
					<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 3}}>
						<Alert severity="error">We could not load search results right now. Please try again shortly.</Alert>
					</Box>
				)}

				<Divider sx={{my: 4}} />

				<Grid container spacing={3} sx={{px: {xs: 2, md: 4, lg: 6}}}>
					<Grid item xs={12} md={7}>
						<RaildaysScrollTopButton />

						{(searchLoading && !items.length) && (
							<Stack alignItems="center" sx={{py: 6}}>
								<Loader />
							</Stack>
						)}

						{(!query && !searchLoading) && (
							<Box sx={{p: {xs: 3, md: 4}, borderRadius: 2, backgroundColor: "#fff", boxShadow: 2}}>
								<Typography align="center">
									Use the search icon in the header to find railways, events and experiences.
								</Typography>
							</Box>
						)}

						{(!searchLoading && query && !items.length) && (
							<Box sx={{p: {xs: 3, md: 4}, borderRadius: 2, backgroundColor: "#fff", boxShadow: 2}}>
								<Typography align="center">No items match your criteria.</Typography>
							</Box>
						)}

						<Stack spacing={3}>
							{itemsWithDistance.map((item, itemIndex) => {
								const name = resolveName(item);
								const railwayName = resolveRailwayName(item, railwaysByOrgId);
								const image = resolveImage(item);
								const location = resolveLocation(item);
								const dateLabel = resolveDateLabel(item);
								const typeLabel = resolveTypeLabel(item);
								const distanceLabel =
									(typeof item?.__distance === "number" && !Number.isNaN(item.__distance)) ?
										formatDistanceMilesKm(item.__distance) :
										"";
								const railwayMeta = railwaysByOrgId.get(String(item?.OrgId || ""));
								const outlineColor = resolveMarkerColor(tab, item, railwayMeta?.typeColor);
								const intro = item?.Intro || item?.Description || "";
								const detailLink = resolveDetailLink(tab, item);

								const position = getItemLocation(item);
								const renderKey = `${resolveItemId(item)}-${position ? `${position[0]}-${position[1]}` : "nopos"}-${itemIndex}`;

								return (
									<Card
										key={renderKey}
										px={0}
										py={0}
										style={{
											backgroundColor: "#fff",
											borderRadius: 12,
											border: `2px solid ${outlineColor}`,
											boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
											overflow: "hidden"
										}}>
										{image && (
											<Image
												alt={name}
												noError={true}
												noLoader={true}
												src={image}
												style={{width: "100%", aspectRatio: "16 / 9"}}
												imgStyles={{width: "100%", height: "100%", display: "block"}} />
										)}
										<CardSection
											display="block"
											px={0}
											py={0}
											style={{padding: CARD_BODY_PADDING}}>
											<Stack spacing={2}>
												<Box>
													<Typography variant="h5" sx={{fontWeight: 800, color: "#e2b534"}}>
														{name}
													</Typography>
													{railwayName && (
														<Typography variant="h6" sx={{fontWeight: 800, color: "#334155", lineHeight: 1.2, mt: 0.25}}>
															{railwayName}
														</Typography>
													)}
													{location && (
														<Typography color="text.secondary" sx={{fontWeight: 700}}>
															{location}
														</Typography>
													)}
													{dateLabel && (
														<Typography color="text.secondary" sx={{fontWeight: 700}}>
															{dateLabel}
														</Typography>
													)}
													{typeLabel && (
														<Typography color="text.secondary">
															{typeLabel}
														</Typography>
													)}
													{distanceLabel && (
														<Typography color="text.secondary" sx={{fontWeight: 700}}>
															{distanceLabel}
														</Typography>
													)}
												</Box>
												{intro && <Typography>{intro}</Typography>}
												{detailLink && (
													<Button label={resolveCardActionLabel(tab)} uri={detailLink} variant="contained" />
												)}
											</Stack>
										</CardSection>
									</Card>
								);
							})}
						</Stack>
					</Grid>

					<Grid item xs={12} md={5}>
						<Box sx={{position: "sticky", top: "var(--raildays-content-top)"}}>
							<RaildaysMap
								markers={mapMarkers}
								bounds={mapBounds}
								markerShape="circle"
								height={MAP_HEIGHT} />
						</Box>
					</Grid>
				</Grid>
			</Container>
		</Box>
	);
};

export default RaildaysSearchView;
